import { reaction } from 'mobx';
import { NoteLayerService } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-mobile/note';
import { tdw } from '@tencent/tencent-doc-report';
import { TDWActionType, TDWModuleType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { countWords } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils/text-util';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import { isSupportNativeTitleBar } from '@src/js/manager/native-titlebar-manager';
import { CommonColor } from '@editor/src/js/constants/common-color';
import { MOBILE_HEAD_HEIGHT } from '@src/js/constant';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { SoftKeyboard } from '@tencent/docs-soft-keyboard';
import {
    NOTE_CANVAS_HEIGHT_CHANGE_STEP,
    NOTE_CANVAS_HEIGHT_CHANGE_TRIGGER,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-mobile/note/note-data';
import { NOTE_COLLAPSE_HEIGHT } from '@src/framework/editor/src/js/constants';
import BaseNoteAreaService from '@src/slide/application/service/note-area-component/core/base/base-note-area-service';
import { IMobileNoteAreaProps } from '@src/slide/application/service/note-area-component/core/mobile/type';
import { IMobileNoteAreaInfo, IMobileNoteAreaServiceDelegates, IMobileNoteAreaServiceLogic } from '@src/slide/application/service/note-area-component/core/mobile/mobile-note-area-service.interface';
import MobileNoteAreaModel from '@src/slide/application/service/note-area-component/core/mobile/mobile-note-area-model';
import BaseViewModel from '@src/slide/infrastructure/module/independent/view-service-define/base/base-view-model';
import type { CSSProperties } from 'react';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';

const MAX_NOTE_HEIGHT = Math.max(document.body.clientHeight, document.body.clientWidth) + 100;

export class MobileNoteAreaService extends BaseNoteAreaService<IMobileNoteAreaProps>
    implements IMobileNoteAreaServiceLogic {
    declare protected services?: IMobileNoteAreaServiceDelegates;
    declare protected model: MobileNoteAreaModel;

    private noteLayer: NoteLayerService;
    private keyboardHeightTimer: ReturnType<typeof setTimeout> | null;
    private leaveMobileFullScreenPending: boolean;
    private softKeyboard: SoftKeyboard;
    private androidKeyBoardShow: boolean; // 不需要放在model里，减少不必要的组件更新
    private scrollPending: boolean;
    private cachedScrollTop: number;
    private topBlankDivHeight: number;
    private bottomBlankDivHeight: number;
    private touchStartY: number;
    private fixedLowIOSContainerStyleFlag: boolean;
    private preventBodyScrollFlag: boolean;
    private lastIsKeyBoardShow: boolean;
    private closeTimer: ReturnType<typeof setTimeout> | null;

    public componentDidMount(): void {
        super.componentDidMount();

        this.androidKeyBoardShow = false;
        this.lastIsKeyBoardShow = false;
        this.cachedScrollTop = 0;
        this.topBlankDivHeight = 0;
        this.bottomBlankDivHeight = 0;

        this.renderCanvasNote();
        this.viewDelegate?.disposeOnUnmount(reaction(
            () => this.services?.noteAreaManager?.show,
            (noteShow) => {
                if (noteShow) {
                    if (this.model.mobileNoteShow) return;
                } else {
                    this.handleMobileNoteExitEditing();
                    if (!this.model.mobileNoteShow) return;
                }

                this.updateModel<MobileNoteAreaModel>({ mobileNoteShow: noteShow });
            },
        ));
        this.viewDelegate?.disposeOnUnmount(reaction(() => this.services?.selectionService?.currentSlide, () => {
            // 监听slide变更
            this.updateModel({});
        }));
        this.viewDelegate?.disposeOnUnmount(reaction(
            () => this.services?.noteAreaManager?.isMobileNoteFullScreen,
            (isMobileNoteFullScreen) => {
                this.updateModel<MobileNoteAreaModel>({
                    editMode: !!isMobileNoteFullScreen,
                });
            },
        ));
        this.noteContainerRef?.addEventListener('scroll', this.handleNoteScroll);
        window.addEventListener('resize', this.leaveMobileFullScreenDelay);
        this.softKeyboard = new SoftKeyboard();
        if (browser.isAndroid) {
            this.softKeyboard.on('change', keyboard => this.onKeyboardChange(keyboard));
        }
        this.addFullScreenModeEvent();
        this.reportNoteSize();
    }

    public componentDidUpdate(): void {
        this.renderCanvasNote();
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', this.leaveMobileFullScreenDelay);
        eventBus.off(EventKeys.IOS_KEY_BOARD_SHOW, this.onKeyBoardShow);
        eventBus.off(EventKeys.IOS_KEY_BOARD_HIDE, this.onKeyBoardHide);
        this.noteContainerRef?.removeEventListener('scroll', this.handleNoteScroll);
    }

    public getNoteAreaInfo = (): IMobileNoteAreaInfo => ({
        noteAreaHeight: this.model.noteAreaHeight,
        mobileNoteShow: this.model.mobileNoteShow,
    });

    public handleMobileNoteEnterEditing = () => {
        if (this.services?.selectionService) {
            this.services.selectionService.lastMouseDownRegion = RegionType.NOTE;
        }
        window.dispatchEvent(new Event('enterEditing'));
    };

    public handleMobileNoteExitEditing = () => {
        window.dispatchEvent(new Event('exitEditing'));
    };

    public enterEditMode = () => {
        this.services?.noteAreaManager?.enterMobileFullScreen();
    };

    public handleButtonClick = () => {
        if (browser.isAndroid && this.leaveMobileFullScreenPending) {
            return;
        }
        this.handleMobileNoteLeaveFullScreen();
    };

    public handleButtonTouchEnd = () => {
        const isEditing = this.services?.editorInstanceService?.checkEditorIsEditing();
        if (isEditing && browser.isAndroid) {
            // 安卓手机在键盘开启时，等待键盘完全关闭收到resize后再关闭备注的全屏模式，否则页面卡顿明显
            (document.activeElement as HTMLElement)?.blur?.();
            this.leaveMobileFullScreenPending = true;
            // 特定机型在app端内可能收不到键盘关闭的resize事件，超过300ms后主动关闭
            if (!this.closeTimer) {
                this.closeTimer = setTimeout(() => {
                    this.leaveMobileFullScreenPending = false;
                    this.handleMobileNoteLeaveFullScreen();
                }, 300);
            }
        }
    };

    public getPopupStyle = (): CSSProperties => {
        if (!this.model.editMode) {
            return {
                position: 'absolute',
                bottom: 0,
                top: 0,
                width: '100%',
                overflow: 'hidden',
            };
        }
        const isNativeTitleBar = isSupportNativeTitleBar();
        return {
            position: 'fixed',
            top: isNativeTitleBar ? `${MOBILE_HEAD_HEIGHT}px` : '0',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 4,
            backgroundColor: '#fff',
            overflow: 'hidden',
            height: isNativeTitleBar ? 'calc(100% - ${MOBILE_HEAD_HEIGHT}px)' : '100%',
        };
    };

    public getContainerStyle = (): CSSProperties => {
        const { editMode, mobileNoteShow, iosKeyboardHeight, noteAreaHeight } = this.model;
        if (!editMode) {
            return {
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: '38px',
                backgroundColor: CommonColor.FFFFFF,
                overflowY: mobileNoteShow ? 'auto' : 'hidden',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                height: noteAreaHeight,
            };
        }
        const isNativeTitleBar = isSupportNativeTitleBar();
        return {
            position: 'absolute',
            top: `${MOBILE_HEAD_HEIGHT}px`,
            bottom: `${(iosKeyboardHeight) + 40}px`,
            left: 0,
            right: 0,
            backgroundColor: CommonColor.FFFFFF,
            overflowY: mobileNoteShow ? 'auto' : 'hidden',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            height: `calc(100% - 74px - ${iosKeyboardHeight + (isNativeTitleBar ? MOBILE_HEAD_HEIGHT : 0)}px)`,
        };
    };

    protected createViewModel(): BaseViewModel<{}> {
        return new MobileNoteAreaModel();
    }

    private addFullScreenModeEvent = () => {
        eventBus.on(EventKeys.IOS_KEY_BOARD_SHOW, this.onKeyBoardShow);
        eventBus.on(EventKeys.IOS_KEY_BOARD_HIDE, this.onKeyBoardHide);
    };

    private onDoubleTap = () => {
        if (!this.model.editMode && this.slideExistence()) {
            const isShowPlaceHolder = this.noteLayer?.isShowPlaceHolder();
            this.enterEditMode();
            isShowPlaceHolder && this.noteLayer?.setFocus();
            tdw({
                module: TDWModuleType.SLIDE_MOBILE_NOTE,
                action: TDWActionType.SLIDE_NOTE_MOBILE_ENTER_EDITING,
            });
        }
    };

    private onKeyboardChange = (keyboard) => {
        if (keyboard.isSupported) {
            const height = keyboard.heightInViewport || keyboard.heightOutOfViewport;
            if (height > 0) {
                this.androidKeyBoardShow = true;
                this.onKeyBoardShow();
            } else {
                this.androidKeyBoardShow = false;
                this.onKeyBoardHide();
            }
        }
    };

    private onKeyBoardShow = (height?: number) => {
        if (this.model.editMode && typeof height === 'number') {
            if (height === 0) {
                if (this.keyboardHeightTimer) return;
                this.keyboardHeightTimer = setTimeout(() => {
                    this.updateModel<MobileNoteAreaModel>({
                        iosKeyboardHeight: 0,
                    });
                }, 50);
            } else {
                this.keyboardHeightTimer && clearTimeout(this.keyboardHeightTimer);
                this.keyboardHeightTimer = null;
                this.updateModel<MobileNoteAreaModel>({
                    iosKeyboardHeight: height,
                });
            }
        }
        if (!this.model.editMode) {
            this.updateModel<MobileNoteAreaModel>({
                tempClose: true,
            });
        }
    };
    private onKeyBoardHide = () => {
        if (this.model.iosKeyboardHeight || this.model.tempClose) {
            this.updateModel<MobileNoteAreaModel>({
                iosKeyboardHeight: 0,
                tempClose: false,
            });
        }
    };

    private handleNoteScroll = (): void => {
        if (this.scrollPending) {
            return;
        }
        if (!this.noteLayer.isLargeNote) {
            return;
        }
        if (!this.model.mobileNoteShow) {
            return;
        }
        if (!this.noteContainerRef) return;
        const containerElem = this.noteContainerRef;
        const { scrollTop } = containerElem;
        const isScrollDown = this.cachedScrollTop < scrollTop;
        const textHeight = this.noteLayer?.getTextAreaHeightSync() || 0;
        if (isScrollDown) {
            // 向下滚动
            this.updateCanvasRenderOffsetScrollDown(containerElem, textHeight);
        } else {
            // 向上滚动
            this.updateCanvasRenderOffsetScrollUp(containerElem, textHeight);
        }
        this.cachedScrollTop = scrollTop;
    };


    private updateCanvasRenderOffsetScrollDown = (containerElem: HTMLDivElement, textHeight: number): void => {
        const { scrollTop, clientHeight } = containerElem;
        // canvas区域已经渲染的，底部剩余的不可见高度
        const canvasBottomDist = MAX_NOTE_HEIGHT -  (scrollTop - this.offsetTop + clientHeight);
        if (canvasBottomDist > NOTE_CANVAS_HEIGHT_CHANGE_TRIGGER) {
            return;
        }
        // 文本在canvas之外还没渲染的，底部剩余的高度
        const textBottomDist = textHeight - this.offsetTop - MAX_NOTE_HEIGHT;
        if (textBottomDist === 0) {
            return;
        }
        // canvas向下移动 200px 或者到文本底部
        this.noteLayer.setOffsetTop(Math.min(scrollTop - NOTE_CANVAS_HEIGHT_CHANGE_STEP, textHeight - MAX_NOTE_HEIGHT));
        this.scrollPending = true;
        this.noteLayer.updateTextEditorProps();
        requestAnimationFrame(() => {
            this.scrollPending = false;
            this.topBlankDivHeight = this.offsetTop;
            this.bottomBlankDivHeight = Math.ceil(textHeight - this.offsetTop - MAX_NOTE_HEIGHT);
            this.setBlankDivHeight(this.topBlankDivHeight, this.bottomBlankDivHeight);
            this.noteContainerRef && (this.noteContainerRef.scrollTop = scrollTop);
            this.noteLayer.updateCacheSelectionRect();
        });
    };

    private updateCanvasRenderOffsetScrollUp = (containerElem: HTMLDivElement, textHeight: number): void => {
        const { scrollTop } = containerElem;
        if (this.offsetTop === 0) {
            return;
        }
        // canvas区域已经渲染的，顶部剩余的不可见高度
        const canvasTopDist = scrollTop - this.offsetTop;
        if (canvasTopDist > NOTE_CANVAS_HEIGHT_CHANGE_TRIGGER) {
            return;
        }
        // canvas向上移动 200px 或者到文本顶部
        this.noteLayer.setOffsetTop(Math.max(scrollTop - NOTE_CANVAS_HEIGHT_CHANGE_STEP, 0));
        this.scrollPending = true;
        this.noteLayer.updateTextEditorProps();
        requestAnimationFrame(() => {
            this.scrollPending = false;
            this.topBlankDivHeight = this.offsetTop;
            this.bottomBlankDivHeight = textHeight - this.offsetTop - MAX_NOTE_HEIGHT;
            this.setBlankDivHeight(this.topBlankDivHeight, this.bottomBlankDivHeight);
            if (this.noteContainerRef) {
                this.noteContainerRef.scrollTop = scrollTop;
            }
            this.noteLayer.updateCacheSelectionRect();
        });
    };


    private setBlankDivHeight = (topHeight, bottomHeight): void => {
        if (!this.noteTopDivRef || !this.noteBottomDivRef) {
            return;
        }
        if (this.noteTopDivRef.style.height === topHeight && this.noteBottomDivRef.style.height === bottomHeight) {
            return;
        }
        this.noteTopDivRef.style.height = `${topHeight || this.topBlankDivHeight}px`;
        this.noteBottomDivRef.style.height = `${bottomHeight || this.bottomBlankDivHeight}px`;
    };

    private updateBlankDivHeight = (textRectHeight: number, applicationHeight: number): void => {
        if (textRectHeight <= MAX_NOTE_HEIGHT) {
            if (this.noteLayer?.isLargeNote) {
                this.resetBlankDiv();
            }
            return;
        }
        const scrollTop = this.noteContainerRef?.scrollTop || 0;
        const topDivHeight = scrollTop;
        const bottomDivHeight = Math.max(textRectHeight - applicationHeight - scrollTop, 0);
        this.setBlankDivHeight(topDivHeight, bottomDivHeight);
    };

    private resetBlankDiv = (): void => {
        this.topBlankDivHeight = 0;
        this.bottomBlankDivHeight = 0;
        this.setBlankDivHeight(0, 0);
        this.noteLayer.setOffsetTop(0);
    };

    private resetContainerScroll = (): void => {
        if (this.noteContainerRef?.scrollTop) {
            this.noteContainerRef.scrollTo(0, 0);
        }
    };

    private resetScroll = () => {
        this.resetBlankDiv();
        this.resetContainerScroll();
    };

    private getTextAreaElemHeight = (): number => (
        this.topBlankDivHeight + this.bottomBlankDivHeight + (this.noteContainerRef?.clientHeight ?? 0)
    );

    private renderCanvasNote(): void {
        if (this.noteCanvasRef && this.props?.mobileEditMode()) {
            const noteAreaWidth = this.noteContainerRef?.getBoundingClientRect?.()?.width || 0;
            let { noteAreaHeight } = this.model;
            const { editMode, iosKeyboardHeight, mobileNoteShow } = this.model;
            if (!mobileNoteShow) {
                noteAreaHeight = NOTE_COLLAPSE_HEIGHT;
            } else if (editMode) {
                noteAreaHeight = this.noteContainerRef?.getBoundingClientRect?.()?.height || noteAreaHeight;
            }
            if (!this.noteLayer && this.noteContainerRef) {
                this.noteLayer = new NoteLayerService({
                    canvasElement: this.noteCanvasRef,
                    containerElement: this.noteContainerRef,
                    width: noteAreaWidth,
                    height: noteAreaHeight,
                    onExitEditing: this.handleMobileNoteExitEditing,
                    onEnterEditing: this.handleMobileNoteEnterEditing,
                    onDoubleTap: this.onDoubleTap,
                    handleCurrentSlideChangeStart: this.resetBlankDiv,
                    handleCurrentSlideChangeEnd: this.resetContainerScroll,
                    resetScroll: this.resetScroll,
                    editMode,
                    handleNoteHeightUpdated: this.updateBlankDivHeight,
                    getTextAreaElemHeight: this.getTextAreaElemHeight,
                    isScrollMode: true,
                });
            } else {
                this.noteLayer.updateWithData(noteAreaWidth, noteAreaHeight, editMode, mobileNoteShow);
                this.updateNoteAreaByKeyboardChange(iosKeyboardHeight || this.androidKeyBoardShow);
                this.fixIOSNativeAction(editMode);
            }
        }
    }

    private updateNoteAreaByKeyboardChange = (IsKeyBoardShow) => {
        const keyboardHasClose = this.lastIsKeyBoardShow && !IsKeyBoardShow;
        const keyboardHasOpen = !this.lastIsKeyBoardShow && IsKeyBoardShow;
        if (keyboardHasOpen && browser.isIOS) {
            this.noteLayer?.scrollToView();
        }
        if (keyboardHasClose) {
            this.noteLayer?.renderNoteFromKayBoardChange();
        }
        this.lastIsKeyBoardShow = IsKeyBoardShow;
    };

    private get offsetTop(): number {
        return this.noteLayer.getOffsetTop();
    }

    private handleMobileNoteLeaveFullScreen = () => {
        const isEditing = this.services?.editorInstanceService?.checkEditorIsEditing();
        if (isEditing) {
            (document.activeElement as HTMLElement)?.blur?.();
        }
        this.services?.noteAreaManager?.leaveMobileFullScreen();
        if (this.closeTimer) {
            clearTimeout(this.closeTimer);
            this.closeTimer = null;
        }
    };

    private leaveMobileFullScreenDelay = () => {
        if (this.leaveMobileFullScreenPending) {
            this.services?.noteAreaManager?.leaveMobileFullScreen();
            this.leaveMobileFullScreenPending = false;
        }
    };

    private fixIOSNativeAction = (editMode: boolean): void => {
        if (browser.isIOS) {
            if (editMode) {
                this.setPreventBodyScroll();
                this.fixLowIOSContainerStyle();
            } else {
                this.clearPreventBodyScroll();
                this.clearFixLowIOSContainerStyle();
            }
        }
    };

    // 阻止ios打开键盘之后，页面整体的滚动，同时不可影响备注本身的滚动
    private setPreventBodyScroll = () => {
        if (!this.preventBodyScrollFlag && browser.isIOS) {
            document.body.addEventListener('touchstart', this.saveTouchStartPosition);
            document.body.addEventListener('touchmove', this.preventBodyScroll, { passive: false });
            this.preventBodyScrollFlag = true;
        }
    };

    private clearPreventBodyScroll = () => {
        if (this.preventBodyScrollFlag) {
            document.body.removeEventListener('touchstart', this.saveTouchStartPosition);
            document.body.removeEventListener('touchmove', this.preventBodyScroll);
            this.preventBodyScrollFlag = false;
        }
    };

    private preventBodyScroll = (e) => {
        if (e.touches[0].clientY < this.touchStartY && this.isCanvasInBottom()) {
            e.preventDefault();
        }
    };

    private fixLowIOSContainerStyle = () => {
        if (this.fixedLowIOSContainerStyleFlag) {
            return;
        }
        const mobileContainerElem = document.getElementById('mobile-container');
        if (mobileContainerElem) {
            mobileContainerElem.style.overflow = 'visible';
            this.fixedLowIOSContainerStyleFlag = true;
        }
    };

    private clearFixLowIOSContainerStyle = () => {
        const mobileContainerElem = document.getElementById('mobile-container');
        if (mobileContainerElem) {
            mobileContainerElem.style.overflow = 'auto';
            this.fixedLowIOSContainerStyleFlag = false;
        }
    };

    private saveTouchStartPosition = (e) => {
        this.touchStartY = e.touches[0].clientY;
    };

    private isCanvasInBottom = (): boolean => {
        const canvasBottom = Math.round(this.noteCanvasRef?.getBoundingClientRect()?.bottom || 0);
        const containerBottom = Math.round(this.noteContainerRef?.getBoundingClientRect()?.bottom || 0);
        return canvasBottom <= containerBottom;
    };

    private slideExistence = () => this.services?.selectionService?.currentSlideIndex !== -1;

    private reportNoteSize = () => {
        let noteMaxSize = 0;
        const slideSize = this.services?.selectionService?.slides.length;
        this.services?.selectionService?.slides.forEach((slide) => {
            const num = countWords(slide.noteData?.noteTextBody?.paragraphList);
            noteMaxSize = Math.max(noteMaxSize, num);
        });
        tdw({
            module: TDWModuleType.SLIDE_MOBILE_NOTE,
            action: TDWActionType.SLIDE_NOTE_SIZE,
            ver6: noteMaxSize,
            ver7: slideSize,
        });
    };
}
